import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Archive, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useApi, errMsg } from "@/lib/api";

function Confirm({ trigger, title, body, action, danger, onConfirm, testId }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent className="bg-[#111319] border-white/10">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-serif-display text-2xl font-normal">{title}</AlertDialogTitle>
          <AlertDialogDescription>{body}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testId}-cancel-btn`}>Keep it</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} data-testid={`${testId}-confirm-btn`}
            className={danger ? "bg-rose-600 hover:bg-rose-500 text-white" : ""}>{action}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function TripRemoveActions({ trip, isAdmin }) {
  const api = useApi();
  const navigate = useNavigate();
  const run = async (fn, msg, opts) => {
    try {
      await fn();
      toast.success(msg, opts);
      navigate("/");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const undo = async () => {
    try {
      await api.post(`/trash/${trip.id}/restore`);
      toast.success(`${trip.ref} restored`);
      navigate(`/trips/${trip.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const undoOpts = isAdmin ? { duration: 8000, action: { label: "Undo", onClick: undo } } : undefined;
  return (
    <div className="lux-card p-5 mt-10 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="trip-remove-actions">
      <p className="flex-1 text-sm text-zinc-400">Remove this trip from the portal.</p>
      <div className="flex flex-wrap gap-2">
        {!trip.archived_at && (
          <Confirm testId="trip-archive" title="Move to the archive?" action="Move to archive"
            body={isAdmin ? "The trip disappears from everyone's dashboard. You can restore it from the archive." : "The trip disappears from everyone's dashboard. Only an admin can restore it."}
            onConfirm={() => run(() => api.post(`/trips/${trip.id}/archive`), "Trip moved to the archive")}
            trigger={<button className="frame-btn" data-testid="trip-archive-btn"><Archive size={13} /> Move to archive</button>} />
        )}
        <Confirm testId="trip-delete" danger title="Delete this trip?" action="Delete"
          body={`${trip.ref} goes to the trash for 7 days${isAdmin ? ", where you can restore it" : ", where an admin can restore it"}. After that it's erased with its options, photos, chat and client links.`}
          onConfirm={() => run(() => api.delete(`/trips/${trip.id}`), "Trip moved to the trash for 7 days", undoOpts)}
          trigger={<button className="frame-btn !text-rose-500 !border-rose-500/50" data-testid="trip-delete-btn"><Trash2 size={13} /> Delete</button>} />
      </div>
    </div>
  );
}
